import Util from '../../../shared/util/util';
import {cloneDeep} from 'lodash';
import {APPROVAL_STATUS} from '../../../../constants';

export const TIME_REG_TYPES = {
	TASK_TIME: 'taskTime',
	PROJECT_TIME: 'projectTime',
	IDLE_TIME: 'idleTime',
	NO_ACCESS: 'noAccess',
};

export const COLUMN_NAME = {
	PROJECT_INDICATOR: 'project-indicator',
	TASK_NAME: 'task-name',
	PROJECT_NAME: 'project-name',
	MILESTONE: 'milestone',
	CLIENT: 'client',
	REMAINING: 'remaining',
	HOURS_INPUT: 'hour-inputs',
	NOTES: 'notes',
	STAR: 'star',
};

export const buildWeekArr = (currentDate, limit) => {
	const weekArray = [];
	const rec = (date, acc, limit) => {
		if (limit > 0) {
			acc.push(date);
			rec(date.clone().subtract(1, 'days'), acc, limit - 1);
		}
		return acc;
	};
	return rec(currentDate, weekArray, limit).reverse();
};

export const addTimeRegTypes = timeRegs => {
	if (timeRegs.length === 0) {
		return [];
	}
	const timeRegsClone = cloneDeep(timeRegs);
	timeRegsClone.forEach(timeReg => {
		const timeRegType = timeReg.node.task && timeReg.node.task.project ? TIME_REG_TYPES.TASK_TIME : timeReg.node.project ? TIME_REG_TYPES.PROJECT_TIME : timeReg.node.idleTime ? TIME_REG_TYPES.IDLE_TIME : null;
		timeReg.node.type = timeRegType;
	});
	return timeRegsClone;
};

export const getInfoTextForHoursInput = (
	noAccess,
	inLockedPeriod,
	approvalStatus,
	invoiced,
	projectDone,
	projectHalted,
	containsNotes,
	hasMultipleHarvstTasks,
	hasAllocation,
	isWeekView,
	isInternalTimeDisabled,
	lockedInTime,
	periodTimeApprovalStatus,
	intl
) => {
	const {formatMessage} = intl;
	const switchToDayText = formatMessage({id: 'timesheets.switch_to_day_view_for_details'});
	if (lockedInTime) {
		if (approvalStatus) {
			if (approvalStatus === APPROVAL_STATUS.APPROVED) return formatMessage({id: 'approval.approved_time'});
			else if (approvalStatus === APPROVAL_STATUS.SUBMITTED) return formatMessage({id: 'approval.submitted_time'});
		} else if (periodTimeApprovalStatus) {
			if (periodTimeApprovalStatus === APPROVAL_STATUS.APPROVED) return formatMessage({id: 'approval.approved_time_input'});
			else if (periodTimeApprovalStatus === APPROVAL_STATUS.SUBMITTED) return formatMessage({id: 'approval.submitted_time_input'});
		}
		return formatMessage({id: 'time_lock.time_reg_locked'});
	}
	if (noAccess) {
		return formatMessage({id: 'overview_time.inaccessible'});
	}
	if (isInternalTimeDisabled) {
		return formatMessage({id: 'timesheets.hours_input.disabled_idle_time'});
	}
	if (hasAllocation) {
		return formatMessage({id: 'timesheets.hours_input.allocation'});
	}
	if (isWeekView && hasMultipleHarvstTasks) {
		return formatMessage({id: 'timesheets.week_view.hours_input.harvest_task'}) + '. ' + switchToDayText;
	}
	if (inLockedPeriod) {
		if (approvalStatus) {
			if (approvalStatus === APPROVAL_STATUS.APPROVED) return formatMessage({id: 'approval.approved_time_unsubmitted'});
			else if (approvalStatus === APPROVAL_STATUS.SUBMITTED) return formatMessage({id: 'approval.submitted_time'});
		}
		return isWeekView ? formatMessage({id: 'timesheets.week_view.hours_input.time_entries_in_locked_period'}) + '. ' + switchToDayText : formatMessage({id: 'timesheets.day_view.hours_input.time_entries_in_locked_period'});
	}
	if (invoiced) {
		return isWeekView ? formatMessage({id: 'timesheets.week_view.hours_input.time_entries_invoiced'}) + '. ' + switchToDayText : formatMessage({id: 'timesheets.day_view.hours_input.time_entries_invoiced'});
	}
	if (projectDone || projectHalted) {
		return formatMessage({id: 'project_section.locked-info'}, {status: formatMessage({id: projectDone ? 'project_status.done' : 'project_status.halted'})});
	}
	if (isWeekView && containsNotes) {
		return formatMessage({id: 'timesheets.week_view.hours_input.time_entries_contains_notes'}) + '. ' + switchToDayText;
	}
};

export const timeEntryContainsHarvestTask = dayRegs => {
	return dayRegs && dayRegs.length > 1 && dayRegs.some(timeReg => timeReg.node && timeReg.node.harvestTask);
};

export const timeEntryInLockedPeriod = (lineItem, dayRegs, isWeekView) => {
	const timeReg = lineItem.timeReg ? (lineItem.timeReg.node ? lineItem.timeReg.node : lineItem.timeReg) : lineItem;
	return isWeekView ? dayRegs && dayRegs.length > 0 && dayRegs.some(tReg => tReg.node.lockedInPeriod) : timeReg.lockedInPeriod;
};

export const getTimeEntryApprovalStatus = (lineItem, dayRegs, isWeekView) => {
	const timeReg = lineItem.timeReg ? (lineItem.timeReg.node ? lineItem.timeReg.node : lineItem.timeReg) : lineItem;
	return isWeekView ? dayRegs && dayRegs.length > 0 && dayRegs.find(tReg => tReg.node.approvalStatus !== APPROVAL_STATUS.NOT_SUBMITTED)?.node.approvalStatus : timeReg.approvalStatus;
};

// hasInvoicedTime, invoiced, locked period etc.
export const timeEntryInvoiced = (lineItem, dayRegs, isWeekView) => {
	// This is kind of gross, but the structures from week/day view are different connections, so we kind of have to
	const timeReg = lineItem.timeReg ? (lineItem.timeReg.node ? lineItem.timeReg.node : lineItem.timeReg) : lineItem;
	const tRegInvoiced = timeReg => timeReg.invoiced || timeReg.xeroInvoiceId || timeReg.economicTimeId;
	return isWeekView ? dayRegs && dayRegs.length > 0 && dayRegs.some(tReg => tRegInvoiced(tReg.node)) : tRegInvoiced(timeReg);
};

export const timeEntryProjectHalted = lineItem => {
	const project = lineItem.project ? lineItem.project : lineItem.task ? lineItem.task.project : lineItem;
	return project.status && project.status === 'HALTED';
};

export const timeEntryProjectDone = lineItem => {
	const project = lineItem.project ? lineItem.project : lineItem.task ? lineItem.task.project : lineItem;
	return project.status && project.status === 'DONE';
};

// Project done/halted, multiple time entries on same day, with notes
export const timeEntryContainsNotes = (lineItem, dayRegs) => {
	const anyNotes = dayRegs && dayRegs.length > 1 && dayRegs.some(timeReg => timeReg.node.notes && timeReg.node.notes.length > 0);
	return anyNotes;
};

export const timeEntryNoAccess = lineItem => {
	// idle times don't have this field so we nee to check if false
	const noAccess = lineItem.fullAccessToProject === false || (lineItem.task && !lineItem.task.fullAccessToProject) | (lineItem.project && !lineItem.project.fullAccessToProject);
	return noAccess;
};

export const lineItemWithNotes = (lineItem, dayRegsForEntitiy) => {
	const notes = [];
	const toMap = dayRegsForEntitiy ? dayRegsForEntitiy : [[], [], [], [], [], [], []];
	toMap.forEach(day => {
		day.forEach(timeReg => {
			const note = {...timeReg.node};
			notes.push(note);
		});
	});

	const lineItemClone = cloneDeep(lineItem);
	lineItemClone.timeRegistrationsWithNotes = notes;
	return lineItemClone;
};

export const groupRegs = timeRegistrations => {
	// Entity ID -> Entity Object
	const seenEntities = new Map();
	// Entity ID -> List of time Regs per day
	const regsMap = new Map();

	timeRegistrations.forEach(timeReg => {
		let keyEntity;
		if (timeReg.node.type === TIME_REG_TYPES.NO_ACCESS) {
			keyEntity = {id: -1};
		} else if (timeReg.node.type === TIME_REG_TYPES.TASK_TIME) {
			keyEntity = timeReg.node.task;
		} else if (timeReg.node.type === TIME_REG_TYPES.PROJECT_TIME) {
			keyEntity = timeReg.node.project;
		} else {
			keyEntity = timeReg.node.idleTime;
		}
		if (keyEntity) {
			// Copy type down to child for use later
			keyEntity.type = timeReg.node.type;
			keyEntity.timeReg = timeReg;

			const dayOfWeek = Util.CreateMomentDate(timeReg.node.year, timeReg.node.month, timeReg.node.day).weekday();

			if (seenEntities.has(keyEntity.id)) {
				const dayRegsForEntitiy = regsMap.get(keyEntity.id);
				dayRegsForEntitiy[dayOfWeek].push(timeReg);
				regsMap.set(keyEntity.id, dayRegsForEntitiy);
			} else {
				const dayRegsForEntitiy = [[], [], [], [], [], [], []];
				dayRegsForEntitiy[dayOfWeek].push(timeReg);
				regsMap.set(keyEntity.id, dayRegsForEntitiy);
				// Set so we know we've seen it, and so we can swap id for object later
				seenEntities.set(keyEntity.id, keyEntity);
			}
		}
	});
	// Zip the above maps into
	// Entity Object -> List of time Regs per day
	const outPutMap = new Map();
	seenEntities.forEach((entity, id) => {
		outPutMap.set(entity, regsMap.get(id));
	});
	return outPutMap;
};

export const groupNoAccessRegs = timeRegistrations => {
	const cpy = [{node: {id: -1, type: TIME_REG_TYPES.NO_ACCESS}}];
	cpy[0].node.minutesRegistered = 0;
	timeRegistrations.forEach(t => {
		if (t.node.type === TIME_REG_TYPES.NO_ACCESS) {
			cpy[0].node.minutesRegistered += t.node.minutesRegistered;
		} else {
			cpy.push(t);
		}
	});
	if (cpy[0].node.minutesRegistered === 0) {
		cpy.shift();
	}
	return cpy;
};

export const filterTimeregistrations = (searchValue, t) => {
	const timeRegistration = t.node ? t.node : t;
	const projectName = timeRegistration.project
		? timeRegistration.project.name + 'P' + timeRegistration.project.companyProjectId
		: timeRegistration.task && timeRegistration.task.project
		? timeRegistration.task.project.name + 'P' + timeRegistration.task.project.companyProjectId
		: '';
	const idleName = timeRegistration.idleTime ? timeRegistration.idleTime.name : '';
	const taskName = timeRegistration.task ? timeRegistration.task.name : timeRegistration.name ? timeRegistration.name : '';
	const milestoneName = timeRegistration.task && timeRegistration.task.phase ? timeRegistration.task.phase.name : timeRegistration.phase ? timeRegistration.phase.name : '';
	const clientName =
		timeRegistration.project && timeRegistration.project.client
			? timeRegistration.project.client.name
			: timeRegistration.task && timeRegistration.task.project && timeRegistration.task.project.client
			? timeRegistration.task.project.client.name
			: '';
	return Util.normalizedIncludes(projectName + idleName + taskName + milestoneName + clientName, searchValue);
};

export const sortList = (list, sortValue, sortAscending) => {
	let sortingList = list;
	const direction = sortAscending ? 1 : -1;

	switch (sortValue) {
		case 'project-indicator':
			sortingList.sort((ak, bk) => {
				// list items are not always stored the same way, have to unwrap
				const a = ak.node ? ak.node : ak;
				const b = bk.node ? bk.node : bk;

				const aId = a.task && a.task.project ? a.task.project.companyProjectId : a.project ? a.project.companyProjectId : -1;
				const bId = b.task && b.task.project ? b.task.project.companyProjectId : b.project ? b.project.companyProjectId : -1;

				/*
					Handle odd cases first
					1 - no pId exist - defaults to using project name or idleTime name
					2 - one of the project-indicator names does not exist or for some reason project and idleTime names are none existing. we place them last
					3 - indicators has same names - sort by project ids
				*/

				// Option 1
				if (aId === -1 && bId === -1) {
					const aName = (a.project && a.project.name) || (a.idleTime && a.idleTime.name) || null;
					const bName = (b.project && b.project.name) || (b.idleTime && b.idleTime.name) || null;
					if (aName != null && bName != null) return aName > bName ? 1 : -1;
				}

				// Option 2
				if (aId === -1 || aId === null) return direction;
				if (bId === -1 || bId === null) return direction * -1;

				//Same project ?
				if (aId === bId) {
					// Same task ?
					const aTaskName = a.task && a.task.name ? a.task.name : a.name ? a.name : a.idleTime ? a.idleTime.name : '';
					const bTaskName = b.task && b.task.name ? b.task.name : b.name ? b.name : b.idleTime ? b.idleTime.name : '';
					if (aTaskName === bTaskName) {
						const aTaskId = a.task ? a.task.id : a.idleTime ? a.idleTime.id : a.id ? a.id : '';
						const bTaskId = b.task ? b.task.id : b.idleTime ? b.idleTime.id : b.id ? b.id : '';
						return aTaskId > bTaskId ? direction : -1 * direction;
					}
					return aTaskName > bTaskName ? direction : direction * -1;
				}

				return aId > bId ? direction : direction * -1;
			});
			break;
		case 'project-name':
			sortingList.sort((ak, bk) => {
				const a = ak.node ? ak.node : ak;
				const b = bk.node ? bk.node : bk;

				const aName = a.task && a.task.project ? a.task.project.name.toLowerCase() : a.project ? a.project.name.toLowerCase() : '';
				const bName = b.task && b.task.project ? b.task.project.name.toLowerCase() : b.project ? b.project.name.toLowerCase() : '';

				/*
					Handle odd cases first
					1 - one of the milestone names does not exist or for some reason project and idleTime names are none existing. we place them last
					2 - project has same names - sort by project ids
				*/

				// Option 1
				if (aName === '' || aName === null) return direction;
				if (bName === '' || bName === null) return direction * -1;

				// Option 2
				if (aName === bName) {
					const aId = a.task && a.task.project ? a.task.project.companyProjectId : a.project ? a.project.companyProjectId : -1;
					const bId = b.task && b.task.project ? b.task.project.companyProjectId : b.project ? b.project.companyProjectId : -1;
					if (aId === bId) {
						const aTaskName = a.task && a.task.name ? a.task.name.toLowerCase() : a.idleTime ? a.idleTime.name.toLowerCase() : a.name ? a.name.toLowerCase() : '';
						const bTaskName = b.task && b.task.name ? b.task.name.toLowerCase() : b.idleTime ? b.idleTime.name.toLowerCase() : b.name ? b.name.toLowerCase() : '';
						if (aTaskName === bTaskName) {
							const aTaskId = a.task ? a.task.id : a.idleTime ? a.idleTime.id : a.id ? a.id : '';
							const bTaskId = b.task ? b.task.id : b.idleTime ? b.idleTime.id : b.id ? b.id : '';
							return aTaskId > bTaskId ? direction : -1 * direction;
						}
						return aTaskName > bTaskName ? direction : -1 * direction;
					}
					return aId > bId ? direction : direction * -1;
				}

				return aName > bName ? direction : direction * -1;
			});
			break;
		case 'task-name':
			sortingList.sort((ak, bk) => {
				const a = ak.node ? ak.node : ak;
				const b = bk.node ? bk.node : bk;

				const aTaskName = a.task && a.task.name ? a.task.name.toLowerCase() : a.idleTime ? a.idleTime.name.toLowerCase() : a.name ? a.name.toLowerCase() : '';
				const bTaskName = b.task && b.task.name ? b.task.name.toLowerCase() : b.idleTime ? b.idleTime.name.toLowerCase() : b.name ? b.name.toLowerCase() : '';
				/*
					Handle odd cases first
					1 - no taks names exist - defaults to using project name or idleTime name
					2 - one of the milestone names does not exist or for some reason project and idleTime names are none existing. we place them last
					3 - task has same names - sort by project ids
				*/

				// Option 1
				if (aTaskName === '' && bTaskName === '') {
					const aName = (a.project && a.project.name) || (a.idleTime && a.idleTime.name) || null;
					const bName = (b.project && b.project.name) || (b.idleTime && b.idleTime.name) || null;
					if (aName != null && bName != null) return aName > bName ? 1 : -1;
				}

				// Option 2
				if (aTaskName === '' || aTaskName === null) return direction;
				if (bTaskName === '' || bTaskName === null) return direction * -1;

				// Option 3
				if (aTaskName === bTaskName) {
					const aProjectName = a.task && a.task.project ? a.task.project.name.toLowerCase() : a.project ? a.project.name.toLowerCase() : a.idleTime ? a.idleTime.name : '';
					const bProjectName = b.task && b.task.project ? b.task.project.name.toLowerCase() : b.project ? b.project.name.toLowerCase() : b.idleTime ? b.idleTime.name : '';
					return aProjectName > bProjectName ? direction : direction * -1;
				}
				return aTaskName > bTaskName ? direction : direction * -1;
			});
			break;
		case 'milestone':
			sortingList.sort((ak, bk) => {
				const a = ak.node ? ak.node : ak;
				const b = bk.node ? bk.node : bk;

				const aMilestoneName = a.task && a.task.phase ? a.task.phase.name.toLowerCase() : a.phase ? a.phase.name.toLowerCase() : '';
				const bMilestoneName = b.task && b.task.phase ? b.task.phase.name.toLowerCase() : b.phase ? b.phase.name.toLowerCase() : '';

				/*
					Handle odd cases first
					1 - no milestone names exist - defaults to using project name or idleTime name
					2 - one of the milestone names does not exist or for some reason project and idleTime names are none existing. we place them last
					3 - milestone has same names - sort by project ids
				*/

				// Option 1
				if (aMilestoneName === '' && bMilestoneName === '') {
					const aName = (a.project && a.project.name) || (a.idleTime && a.idleTime.name) || null;
					const bName = (b.project && b.project.name) || (b.idleTime && b.idleTime.name) || null;
					if (aName != null && bName != null) return aName > bName ? 1 : -1;
				}

				// Option 2
				if (aMilestoneName === '' || aMilestoneName === null) return direction;
				if (bMilestoneName === '' || bMilestoneName === null) return direction * -1;

				// Option 3
				if (aMilestoneName === bMilestoneName) {
					const aId = a.task && a.task.project ? a.task.project.companyProjectId : a.project ? a.project.companyProjectId : -1;
					const bId = b.task && b.task.project ? b.task.project.companyProjectId : b.project ? b.project.companyProjectId : -1;
					// Same project?
					if (aId === bId) {
						// Same task?
						const aTaskName = a.task && a.task.name ? a.task.name.toLowerCase() : a.idleTime ? a.idleTime.name.toLowerCase() : a.name ? a.name.toLowerCase() : '';
						const bTaskName = b.task && b.task.name ? b.task.name.toLowerCase() : b.idleTime ? b.idleTime.name.toLowerCase() : b.name ? b.name.toLowerCase() : '';
						return aTaskName > bTaskName ? direction : -1 * direction;
					}
					return aId > bId ? direction : direction * -1;
				}

				return aMilestoneName > bMilestoneName ? direction : direction * -1;
			});
			break;
		case 'client':
			sortingList.sort((ak, bk) => {
				const a = ak.node ? ak.node : ak;
				const b = bk.node ? bk.node : bk;

				const aClientName = a.task && a.task.project && a.task.project.client ? a.task.project.client.name.toLowerCase() : a.project && a.project.client ? a.project.client.name.toLowerCase() : '';
				const bClientName = b.task && b.task.project && b.task.project.client ? b.task.project.client.name.toLowerCase() : b.project && b.project.client ? b.project.client.name.toLowerCase() : '';

				/*
					Handle odd cases first
					1 - no client names exist - defaults to using project name or idleTime name
					2 - one of the client names does not exist or for some reason project and idleTime names are none existing. we place them last
					3 - client has same names - sort by project ids
				*/

				// Option 1
				if (aClientName === '' && bClientName === '') {
					const aName = (a.project && a.project.name) || (a.idleTime && a.idleTime.name) || null;
					const bName = (b.project && b.project.name) || (b.idleTime && b.idleTime.name) || null;
					if (aName != null && bName != null) return aName > bName ? 1 : -1;
				}

				// Option 2
				if (aClientName === '' || aClientName === null) return direction;
				if (bClientName === '' || bClientName === null) return direction * -1;

				// Option 3
				if (aClientName === bClientName) {
					const aId = a.task && a.task.project ? a.task.project.companyProjectId : a.project ? a.project.companyProjectId : -1;
					const bId = b.task && b.task.project ? b.task.project.companyProjectId : b.project ? b.project.companyProjectId : -1;
					// Same project?
					if (aId === bId) {
						// Same task?
						const aTaskName = a.task && a.task.name ? a.task.name.toLowerCase() : a.idleTime ? a.idleTime.name.toLowerCase() : a.name ? a.name.toLowerCase() : '';
						const bTaskName = b.task && b.task.name ? b.task.name.toLowerCase() : b.idleTime ? b.idleTime.name.toLowerCase() : b.name ? b.name.toLowerCase() : '';
						return aTaskName > bTaskName ? direction : -1 * direction;
					}
					return aId > bId ? direction : direction * -1;
				}

				return aClientName > bClientName ? direction : direction * -1;
			});
			break;
		case 'remaining':
			sortingList.sort((ak, bk) => {
				const a = ak.node ? ak.node : ak;
				const b = bk.node ? bk.node : bk;
				let aRem = a.task && a.task.timeLeft != null ? a.task.timeLeft : a.timeLeft != null ? a.timeLeft : -1;
				let bRem = b.task && b.task.timeLeft != null ? b.task.timeLeft : b.timeLeft != null ? b.timeLeft : -1;

				// sort project name / idleTime name if no timeleft exists - always ascending
				if (aRem === -1 && bRem === -1) {
					const aName = (a.project && a.project.name) || (a.idleTime && a.idleTime.name) || null;
					const bName = (b.project && b.project.name) || (b.idleTime && b.idleTime.name) || null;
					if (aName != null && bName != null) return aName > bName ? 1 : -1;
				}
				return aRem > bRem ? direction : direction * -1;
			});
			break;
		default:
			break;
	}
	return sortingList;
};
